import { useSelector } from 'react-redux';
import { getRegions, getStores } from '../../api/suApi';
import type { GroupBy, Org, Period, RegionsResponse, StoresResponse } from '../../api/types';
import { useAsync, AsyncState } from '../../hooks/useAsync';
import { selectOrg, selectStoreType } from '../../state/filtersSlice';

/**
 * Region breakdown that ignores the region/city filters (so a state dropdown
 * or chart still lists every state once a state is selected). Honours store type.
 * Pass `region` to list only that state's cities (State Wise drill-down).
 */
export function useRegionBreakdown(
  groupBy: GroupBy, period: Period, enabled = true, region?: string | null,
): AsyncState<RegionsResponse> {
  const storeType = useSelector((s: any) => selectStoreType(s)) as string;
  const org = useSelector((s: any) => selectOrg(s)) as Org;
  const params = { period, group_by: groupBy, region: region || undefined, store_type: storeType === 'all' ? undefined : storeType };
  return useAsync((signal) => getRegions(params, { signal, org }), `rb:${org}:${JSON.stringify(params)}`, enabled);
}

/** Network-wide store list for search (limit 200), ignoring region/city filters. */
export function useAllStores(period: Period, enabled = true): AsyncState<StoresResponse> {
  const storeType = useSelector((s: any) => selectStoreType(s)) as string;
  const org = useSelector((s: any) => selectOrg(s)) as Org;
  const params = {
    period,
    store_type: storeType === 'all' ? undefined : storeType,
    sort: 'sales' as const,
    order: 'desc' as const,
    limit: 200,
    offset: 0,
  };
  return useAsync((signal) => getStores(params, { signal, org }), `all:${JSON.stringify(params)}`, enabled);
}
